import React, { useEffect, useLayoutEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import SEO from '../components/SEO';
import { getPhotoUrl } from '../utils/bucketUrl';
import BackgroundVideo from '../components/Home/BackgroundVideo';
import { HOME_VIDEO_CLIPS } from '../config/homeMedia';
import './Home.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const YOUTUBE_CACHE_KEY = 'mr_youtube_videos_cache';
const YOUTUBE_CACHE_TTL_MS = 15 * 60 * 1000;
const IG_CACHE_KEY = 'mr_ig_posts_cache';
const IG_CACHE_TTL_MS = 60 * 1000;

const Home = () => {
  const { t } = useLanguage();
  const reducedMotion = useReducedMotion();
  const reveal = {
    initial: reducedMotion ? false : { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.15 },
    transition: { duration: 0.6 },
  };
  const [igPosts, setIgPosts] = useState(null); // null = not yet loaded
  const [youtubeVideos, setYoutubeVideos] = useState([]);
  const [loadingVideos, setLoadingVideos] = useState(true);

  useLayoutEffect(() => {
    const root = document.documentElement;
    const navbar = document.querySelector('.home-navbar');
    const previousHeight = root.style.getPropertyValue('--home-nav-height');
    root.classList.add('home-scroll-page');

    const updateNavHeight = () => {
      root.style.setProperty('--home-nav-height', `${navbar?.getBoundingClientRect().height || 0}px`);
    };
    updateNavHeight();
    const observer = new ResizeObserver(updateNavHeight);
    if (navbar) observer.observe(navbar);

    return () => {
      observer.disconnect();
      root.classList.remove('home-scroll-page');
      if (previousHeight) root.style.setProperty('--home-nav-height', previousHeight);
      else root.style.removeProperty('--home-nav-height');
    };
  }, []);

  useEffect(() => {
    const fetchSocialContent = async () => {
      // 1. Try Instagram posts first
      try {
        const cachedIg = localStorage.getItem(IG_CACHE_KEY);
        if (cachedIg) {
          const parsed = JSON.parse(cachedIg);
          if (parsed?.timestamp && Array.isArray(parsed?.posts) && (Date.now() - parsed.timestamp) < IG_CACHE_TTL_MS) {
            setIgPosts(parsed.posts);
            setLoadingVideos(false);
            return;
          }
        }
      } catch {
        // ignore cache read errors
      }

      try {
        const igRes = await fetch(`${API_URL}/api/instagram/posts`);
        if (igRes.ok) {
          const data = await igRes.json();
          if (Array.isArray(data) && data.length > 0) {
            setIgPosts(data);
            setLoadingVideos(false);
            try {
              localStorage.setItem(IG_CACHE_KEY, JSON.stringify({ timestamp: Date.now(), posts: data }));
            } catch {
              // ignore
            }
            return;
          }
        }
      } catch {
        // fall through to YouTube
      }

      // 2. Fallback: fetch YouTube videos
      setIgPosts([]);
      try {
        const cachedYt = localStorage.getItem(YOUTUBE_CACHE_KEY);
        if (cachedYt) {
          const parsed = JSON.parse(cachedYt);
          if (parsed?.timestamp && Array.isArray(parsed?.videos) && (Date.now() - parsed.timestamp) < YOUTUBE_CACHE_TTL_MS) {
            setYoutubeVideos(parsed.videos);
            setLoadingVideos(false);
            return;
          }
        }
      } catch {
        // ignore
      }

      try {
        const ytRes = await fetch(`${API_URL}/api/youtube/videos`);
        if (ytRes.ok) {
          const data = await ytRes.json();
          setYoutubeVideos(data);
          try {
            localStorage.setItem(YOUTUBE_CACHE_KEY, JSON.stringify({ timestamp: Date.now(), videos: data }));
          } catch {
            // ignore
          }
        }
      } catch (error) {
        console.error('Error fetching YouTube videos:', error);
        setYoutubeVideos([]);
      } finally {
        setLoadingVideos(false);
      }
    };
    fetchSocialContent();
  }, []);

  const services = [
    { number: '01', title: 'nav.tuning', description: 'home.premiumDesc', image: 'IMG_1802.JPG', to: '/tuning' },
    { number: '02', title: 'nav.workshop', description: 'home.diagnosticsDesc', image: 'IMG_6561.jpeg', to: '/workshop' },
    { number: '03', title: 'nav.shop', description: 'home.experience.shopDescription', image: 'IMG_0977.jpeg', to: '/shop' },
  ];
  const instagram = igPosts?.length > 0;
  const socialItems = instagram ? igPosts : youtubeVideos;
  const instagramUrl = process.env.REACT_APP_INSTAGRAM_URL || 'https://www.instagram.com/meller_racing_/';

  return (
    <div className="home-page">
      <SEO title={t('seo.home.title')} description={t('seo.home.description')} keywords={t('seo.home.keywords')} />

      <section className="home-hero" aria-labelledby="home-hero-title">
        <BackgroundVideo clips={HOME_VIDEO_CLIPS} />
        <div className="home-shell home-hero-inner">
          <div className="home-hero-topline">
            <p className="home-eyebrow"><span className="home-status-dot" /> {t('home.experience.heroEyebrow')}</p>
            <span className="home-location">Suszec, PL</span>
          </div>
          <motion.div
            className="home-hero-copy"
            initial={reducedMotion ? false : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="home-hero-signature">Meller Racing</p>
            <h1 id="home-hero-title">{t('home.heroTitle')}<br /><span>{t('home.heroPower')}</span><span className="home-title-period">.</span></h1>
            <p className="home-hero-description">{t('home.heroDescription')}</p>
            <div className="home-hero-actions">
              <Link to="/tuning" className="home-button home-button-primary">{t('home.viewTuners')} <Arrow /></Link>
              <Link to="/tuning-points" className="home-button home-button-outline">{t('home.shopNow')} <Arrow /></Link>
            </div>
          </motion.div>
          <div className="home-hero-bottom">
            <a href="#home-services" className="home-scroll-link"><span className="home-scroll-line" /> {t('home.experience.discover')}</a>
            <span className="home-hero-caption">{t('home.experience.heroSignature')}</span>
          </div>
        </div>
      </section>

      <section className="home-section home-services" id="home-services" aria-labelledby="home-services-title">
        <div className="home-shell">
          <motion.div className="home-section-heading" {...reveal}>
            <div><p className="home-eyebrow">01 / {t('home.experience.servicesEyebrow')}</p><h2 id="home-services-title">{t('home.experience.servicesTitle')}</h2></div>
            <p className="home-section-intro">{t('home.experience.servicesDescription')}</p>
          </motion.div>
          <div className="home-services-grid">
            {services.map((service) => (
              <motion.div key={service.number} {...reveal}>
                <Link to={service.to} className="home-service-card">
                  <img src={getPhotoUrl(service.image)} alt="" loading="lazy" decoding="async" />
                  <div className="home-card-shade" />
                  <span className="home-service-number">/{service.number}</span>
                  <div className="home-service-content"><h3>{t(service.title)}</h3><p>{t(service.description)}</p><span className="home-card-link">{t('home.experience.explore')} <Arrow /></span></div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="home-work" aria-labelledby="home-work-title">
        <div className="home-work-image"><img src={getPhotoUrl('IMG_3938.jpeg')} alt={t('home.premiumTitle')} loading="lazy" decoding="async" /><div className="home-work-shade" /></div>
        <div className="home-shell home-work-inner">
          <motion.div className="home-work-copy" {...reveal}>
            <p className="home-eyebrow">02 / {t('home.ourWork')} {t('home.work')}</p>
            <h2 id="home-work-title">{t('home.premiumTitle')}<span className="home-title-period">.</span></h2>
            <p>{t('home.premiumDesc')}</p>
            <Link to="/contact" className="home-button home-button-outline">{t('home.experience.talkToUs')} <Arrow /></Link>
          </motion.div>
          <span className="home-work-wordmark" aria-hidden="true">MELLER RACING</span>
        </div>
      </section>

      <section className="home-section home-details" aria-labelledby="home-details-title">
        <div className="home-shell">
          <motion.div className="home-section-heading" {...reveal}>
            <div><p className="home-eyebrow">03 / {t('home.experience.detailsEyebrow')}</p><h2 id="home-details-title">{t('home.experience.detailsTitle')}</h2></div>
            <Link to="/workshop" className="home-text-link">{t('nav.workshop')} <Arrow /></Link>
          </motion.div>
          <div className="home-detail-grid">
            {[
              { image: 'IMG_3934.jpeg', title: 'home.trackReadyTitle', description: 'home.trackReadyDesc', to: '/contact' },
              { image: 'IMG_6561.jpeg', title: 'home.diagnosticsTitle', description: 'home.diagnosticsDesc', to: '/workshop' },
            ].map((item) => (
              <motion.div key={item.image} {...reveal}>
                <Link className="home-detail-card" to={item.to}>
                  <div className="home-detail-image"><img src={getPhotoUrl(item.image)} alt="" loading="lazy" decoding="async" /></div>
                  <div className="home-detail-content"><div><h3>{t(item.title)}</h3><p>{t(item.description)}</p></div><Arrow /></div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="home-section home-gallery" aria-labelledby="home-gallery-title">
        <div className="home-shell">
          <motion.div className="home-section-heading" {...reveal}>
            <div><p className="home-eyebrow">04 / {t('home.experience.galleryEyebrow')}</p><h2 id="home-gallery-title">{t('home.customSolutions')}</h2></div>
            <Link to="/shop" className="home-text-link">{t('nav.shop')} <Arrow /></Link>
          </motion.div>
          <div className="home-photo-strip">
            {[
              { image: 'IMG_007.jpeg', title: 'home.experience.galleryConnections' },
              { image: 'IMG_0977.jpeg', title: 'home.premiumParts' },
              { image: 'IMG_9267.jpeg', title: 'home.racingGear' },
            ].map((item) => (
              <motion.figure key={item.image} {...reveal}>
                <img src={getPhotoUrl(item.image)} alt="" loading="lazy" decoding="async" />
                <figcaption>{t(item.title)}</figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>

      <section className="home-section home-social" aria-labelledby="home-social-title">
        <div className="home-shell">
          <motion.div className="home-section-heading" {...reveal}>
            <div><p className="home-eyebrow">05 / {t('home.experience.socialEyebrow')}</p><h2 id="home-social-title">{instagram ? <>{t('home.latestPosts')} <span>Instagram</span></> : t('home.latestVideos')}</h2></div>
            <a href={instagram ? instagramUrl : 'https://www.youtube.com/@meller_racing'} target="_blank" rel="noopener noreferrer" className="home-text-link">{t(instagram ? 'home.followOnInstagram' : 'home.visitChannel')} <Arrow /></a>
          </motion.div>
          {loadingVideos ? (
            <div className="home-social-grid" aria-busy="true">
              {[0, 1, 2, 3].map((index) => <div key={index} className="home-social-placeholder" />)}
            </div>
          ) : socialItems.length > 0 ? (
            <div className={`home-social-grid${instagram ? ' home-social-instagram' : ''}`}>
              {socialItems.map((item) => (
                <motion.a key={item.id} href={instagram ? item.post_url : item.url} target="_blank" rel="noopener noreferrer" className="home-social-card" {...reveal}>
                  <div className={`home-social-image${instagram ? ' is-square' : ''}`}>
                    <img src={instagram ? item.image_url : item.thumbnail} alt={instagram ? (item.caption || t('home.experience.instagramPost')) : item.title} loading="lazy" decoding="async" />
                    <span className="home-social-icon"><Arrow /></span>
                  </div>
                  {(item.caption || item.title) && <h3>{instagram ? item.caption : item.title}</h3>}
                  {!instagram && <p>{t('home.watchOnYouTube')}</p>}
                </motion.a>
              ))}
            </div>
          ) : (
            <p className="home-social-empty">{t('home.latestVideosDesc')}</p>
          )}
        </div>
      </section>

      <section className="home-contact" aria-labelledby="home-contact-title">
        <div className="home-shell home-contact-inner">
          <motion.div {...reveal}><p className="home-eyebrow">{t('home.experience.contactEyebrow')}</p><h2 id="home-contact-title">{t('home.experience.contactTitle')}<span className="home-title-period">?</span></h2><p className="home-contact-description">{t('home.experience.contactDescription')}</p></motion.div>
          <Link to="/contact" className="home-button home-button-primary">{t('home.experience.talkToUs')} <Arrow /></Link>
        </div>
      </section>
    </div>
  );
};

const Arrow = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><path d="M4 12h15M13 5l7 7-7 7" /></svg>
);

export default Home;
